// src/components/Toaster/Toaster.js
import { TOASTER_CONFIG } from "../../utils/constants.js";
import "./Toaster.css";

class Toaster {
  constructor() {
    this.toasts = [];
    this.history = [];
    this.container = null;
    this.toastContainer = null;
    this.historyModal = null;
    this.historyList = null;
    this.mounted = false;
  }

  mount(selector) {
    if (this.mounted) {
      return;
    }

    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Toaster container not found: ${selector}`);
    }

    this.init();
    this.mounted = true;
  }

  unmount() {
    if (!this.mounted) return;

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    if (this.historyModal && this.historyModal.parentNode) {
      this.historyModal.parentNode.removeChild(this.historyModal);
    }

    this.toasts = [];
    this.container = null;
    this.toastContainer = null;
    this.historyModal = null;
    this.historyList = null;
    this.mounted = false;
  }

  init() {
    // Create toaster container (just for active toasts)
    this.container = document.createElement("div");
    this.container.className = "toaster";
    this.container.setAttribute("aria-live", "polite");
    this.container.setAttribute("aria-atomic", "false");

    // Add to DOM
    document.body.appendChild(this.container);

    // Create toast container (no header needed for active toasts)
    this.toastContainer = document.createElement("div");
    this.toastContainer.className = "toaster__container";
    this.container.appendChild(this.toastContainer);

    // Create history modal
    this.createHistoryModal();

    // Bind to existing history button
    this.bindHistoryButton();
  }

  createHistoryModal() {
    // Create modal overlay
    this.historyModal = document.createElement("div");
    this.historyModal.className = "toaster-history-modal";
    this.historyModal.style.display = "none";

    // Create modal content
    const modalContent = document.createElement("div");
    modalContent.className = "toaster-history-content";

    // Header
    const header = document.createElement("div");
    header.className = "toaster-history-header";

    const title = document.createElement("h2");
    title.textContent = "Historique des messages";

    const closeBtn = document.createElement("button");
    closeBtn.className = "toaster-history-close";
    closeBtn.innerHTML = "×";
    closeBtn.addEventListener("click", () => this.hideHistory());

    header.appendChild(title);
    header.appendChild(closeBtn);

    // History list container
    this.historyList = document.createElement("div");
    this.historyList.className = "toaster-history-list";

    // Actions
    const actions = document.createElement("div");
    actions.className = "toaster-history-actions";

    const clearHistoryBtn = document.createElement("button");
    clearHistoryBtn.className = "btn-clear-history";
    clearHistoryBtn.textContent = "Vider l'historique";
    clearHistoryBtn.addEventListener("click", () => {
      this.clearHistory();
      this.renderHistory();
    });

    actions.appendChild(clearHistoryBtn);

    modalContent.appendChild(header);
    modalContent.appendChild(this.historyList);
    modalContent.appendChild(actions);
    this.historyModal.appendChild(modalContent);

    // Close on overlay click
    this.historyModal.addEventListener("click", (e) => {
      if (e.target === this.historyModal) {
        this.hideHistory();
      }
    });

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.historyModal && this.historyModal.style.display === "flex") {
        this.hideHistory();
      }
    });

    document.body.appendChild(this.historyModal);
  }

  bindHistoryButton() {
    // Wait for DOM to be ready, then bind to existing history button
    const bindButton = () => {
      const historyBtn = document.getElementById("history-btn");
      if (historyBtn) {
        historyBtn.addEventListener("click", () => this.showHistory());
        // Update button title to reflect new functionality
        historyBtn.title = "Historique des messages de notification";
      } else {
        // If button not found, try again after a short delay
        setTimeout(bindButton, 100);
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bindButton);
    } else {
      bindButton();
    }
  }

  showHistory() {
    if (!this.historyModal) return;
    this.renderHistory();
    this.historyModal.style.display = "flex";
    // Focus management for accessibility
    this.historyModal.querySelector(".toaster-history-close").focus();
  }

  hideHistory() {
    if (!this.historyModal) return;
    this.historyModal.style.display = "none";
  }

  renderHistory() {
    if (!this.historyList) return;

    this.historyList.innerHTML = "";

    if (this.history.length === 0) {
      const emptyMsg = document.createElement("div");
      emptyMsg.className = "toaster-history-empty";
      emptyMsg.textContent = "Aucun message dans l'historique";
      this.historyList.appendChild(emptyMsg);
      return;
    }

    this.history.forEach((toast) => {
      const historyItem = document.createElement("div");
      historyItem.className = `toaster-history-item toaster-history-item--${toast.type}`;

      const icon = document.createElement("div");
      icon.className = "toaster-history-icon";
      icon.textContent = this.getIcon(toast.type);

      const content = document.createElement("div");
      content.className = "toaster-history-content-text";

      const message = document.createElement("div");
      message.className = "toaster-history-message";
      message.textContent = toast.message;

      const timestamp = document.createElement("div");
      timestamp.className = "toaster-history-timestamp";
      timestamp.textContent = this.formatFullTime(toast.timestamp);

      content.appendChild(message);
      content.appendChild(timestamp);

      historyItem.appendChild(icon);
      historyItem.appendChild(content);
      this.historyList.appendChild(historyItem);
    });
  }

  addToast(message, type = TOASTER_CONFIG.types.INFO, options = {}) {
    if (!this.mounted) {
      console.warn("Toaster not mounted, cannot add toast");
      return;
    }

    const id = Date.now() + Math.random();
    const duration = options.duration ?? TOASTER_CONFIG.duration;

    const toast = {
      id,
      message,
      type,
      timestamp: new Date(),
      duration,
      persistent: options.persistent || false,
    };

    this.toasts.push(toast);

    // Add to history (limit to maxHistory)
    this.history.unshift(toast);
    if (this.history.length > TOASTER_CONFIG.maxHistory) {
      this.history = this.history.slice(0, TOASTER_CONFIG.maxHistory);
    }

    // Create DOM element
    this.createToastElement(toast);

    // Auto-remove toast if not persistent
    if (!toast.persistent && duration > 0) {
      setTimeout(() => {
        this.removeToast(id);
      }, duration);
    }

    return id;
  }

  createToastElement(toast) {
    if (!this.toastContainer) return;

    const toastEl = document.createElement("div");
    toastEl.className = `toast toast--${toast.type}`;
    toastEl.setAttribute("role", "alert");
    toastEl.dataset.id = toast.id;

    // Create content
    const content = document.createElement("div");
    content.className = "toast__content";

    // Icon
    const icon = document.createElement("div");
    icon.className = "toast__icon";
    icon.textContent = this.getIcon(toast.type);

    // Body
    const body = document.createElement("div");
    body.className = "toast__body";

    const message = document.createElement("div");
    message.className = "toast__message";
    message.textContent = toast.message;

    const timestamp = document.createElement("div");
    timestamp.className = "toast__timestamp";
    timestamp.textContent = this.formatTime(toast.timestamp);

    body.appendChild(message);
    body.appendChild(timestamp);

    // Close button
    const closeBtn = document.createElement("button");
    closeBtn.className = "toast__close";
    closeBtn.innerHTML = "×";
    closeBtn.setAttribute("aria-label", "Close notification");
    closeBtn.addEventListener("click", () => this.removeToast(toast.id));

    content.appendChild(icon);
    content.appendChild(body);
    content.appendChild(closeBtn);
    toastEl.appendChild(content);

    // Progress bar for non-persistent toasts
    if (!toast.persistent && toast.duration > 0) {
      const progress = document.createElement("div");
      progress.className = "toast__progress";
      progress.style.setProperty("--duration", `${toast.duration}ms`);
      toastEl.appendChild(progress);
    }

    // Add to container with animation
    this.toastContainer.appendChild(toastEl);

    // Trigger animation
    requestAnimationFrame(() => {
      toastEl.classList.add("toast--visible");
    });
  }

  removeToast(id) {
    const toastIndex = this.toasts.findIndex((t) => t.id === id);
    if (toastIndex === -1) return;

    this.toasts.splice(toastIndex, 1);

    if (this.toastContainer) {
      const toastEl = this.toastContainer.querySelector(`[data-id="${id}"]`);
      if (toastEl) {
        toastEl.classList.add("toast--removing");
        setTimeout(() => {
          if (toastEl.parentNode) {
            toastEl.parentNode.removeChild(toastEl);
          }
        }, 300);
      }
    }
  }

  clearAll() {
    this.toasts = [];
    if (this.toastContainer) {
      this.toastContainer.innerHTML = "";
    }
  }

  clearHistory() {
    this.history = [];
  }

  getIcon(type) {
    switch (type) {
      case TOASTER_CONFIG.types.SUCCESS:
        return "✓";
      case TOASTER_CONFIG.types.WARNING:
        return "⚠";
      case TOASTER_CONFIG.types.ERROR:
        return "✕";
      case TOASTER_CONFIG.types.INFO:
      default:
        return "ℹ";
    }
  }

  formatTime(timestamp) {
    return timestamp.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  formatFullTime(timestamp) {
    return timestamp.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  // Convenience methods
  showInfo(message, options) {
    return this.addToast(message, TOASTER_CONFIG.types.INFO, options);
  }

  showSuccess(message, options) {
    return this.addToast(message, TOASTER_CONFIG.types.SUCCESS, options);
  }

  showWarning(message, options) {
    return this.addToast(message, TOASTER_CONFIG.types.WARNING, options);
  }

  showError(message, options) {
    return this.addToast(message, TOASTER_CONFIG.types.ERROR, options);
  }

  // Getters
  getToasts() {
    return [...this.toasts];
  }

  getHistory() {
    return [...this.history];
  }
}

export default Toaster;
